import { jobLabel, type Eta } from "@veyso/core";

/**
 * Time left as the screen shows it.
 *
 * The API sends a fresh estimate on every poll, and two machines answer in
 * turn, so the raw number wobbles. Shown as it arrives, "4 minutes" would
 * become "6", then "3". Here a new estimate pulls the shown one by at most
 * 30%, and between polls the shown one counts down, so it neither jumps nor
 * stands still.
 */
export interface Shown {
  seconds: number;
  at: number;
}

const MAX_STEP = 0.3;
const MIN_STEP_SECONDS = 10;

export function countdown(shown: Shown, now: number): number {
  return Math.max(0, shown.seconds - (now - shown.at) / 1000);
}

export function smooth(prev: Shown | undefined, next: number, now: number): Shown {
  if (!prev) return { seconds: next, at: now };
  const current = countdown(prev, now);
  const limit = Math.max(current * MAX_STEP, MIN_STEP_SECONDS);
  const moved = current + Math.max(-limit, Math.min(limit, next - current));
  return { seconds: Math.round(moved), at: now };
}

export function smoothedEta(
  prev: Shown | undefined,
  eta: Eta | null | undefined,
  now: number,
): { shown: Shown | undefined; eta: Eta | null } {
  if (!eta || eta.kind !== "estimate") return { shown: undefined, eta: eta ?? null };
  const shown = smooth(prev, eta.seconds, now);
  return { shown, eta: { kind: "estimate", seconds: shown.seconds } };
}

/** What is remembered per job: the shown value, and when an estimate last moved it. */
export interface Held {
  shown: Shown;
  last: number;
}

/** Every estimate object already folded into a memory, one object per poll. */
const foldedInto = new WeakMap<Map<string, Held>, WeakSet<Eta>>();

/**
 * Fold one poll's estimate into the shared memory. The memory is shared by
 * every place showing the job — the page from its poll, the jobs chip from
 * the feed's — so each estimate object is folded once, ever, however many
 * components or renders see it. Remembering only the last one would let two
 * sources re-fold each other on every tick and stop the countdown. A place
 * with no estimate yet (null) changes nothing; a new "slow" or "typical"
 * from a poll ends the countdown.
 */
export function foldEta(memory: Map<string, Held>, key: string, eta: Eta | null | undefined, now: number): void {
  if (!eta) return;
  let folded = foldedInto.get(memory);
  if (!folded) foldedInto.set(memory, (folded = new WeakSet()));
  if (folded.has(eta)) return;
  folded.add(eta);
  const result = smoothedEta(memory.get(key)?.shown, eta, now);
  if (result.shown) memory.set(key, { shown: result.shown, last: now });
  else memory.delete(key);
  // Keys are job ids, and a job that finished is never asked about again, so
  // anything not polled for a while is dropped rather than kept for the tab's life.
  for (const [other, held] of memory) if (now - held.last > FORGET_MS) memory.delete(other);
}

const FORGET_MS = 10 * 60_000;

export function emailNote(email: string | null, when: string): string | null {
  return email ? `You can leave this page — we'll email ${email} when ${when}.` : null;
}

type Feed = { id: string; emailsYou?: boolean }[];

/**
 * Whether the jobs feed says this job will email the person looking: a
 * teammate watching somebody else's job, or a revoice, gets nothing.
 */
export function feedEmailsYou(feed: Feed, jobId: string | null | undefined): boolean {
  return jobId != null && feed.some((job) => job.id === jobId && job.emailsYou === true);
}

/** The email line, only for a job that will send it. */
export function emailNoteFor(
  feed: Feed,
  jobId: string | null | undefined,
  email: string | null,
  when: string,
): string | null {
  return feedEmailsYou(feed, jobId) ? emailNote(email, when) : null;
}

/** A server export's line, which after it ends says how, not "Rendering the video 100%". */
export function serverRenderLabel(status: string, step: string | null): string {
  if (status === "completed") return "Finished";
  if (status === "cancelled") return "Cancelled";
  return jobLabel("render", step) ?? "Rendering the video";
}

export const BROWSER_NOTE = "Keep this tab open — this one runs on your computer, so closing it stops it.";

/** Remotion's own estimate for a render in this tab, which it works out from frames encoded so far. */
export function browserEta(estimatedMsRemaining: number | null): Eta {
  return estimatedMsRemaining === null
    ? { kind: "unknown" }
    : { kind: "estimate", seconds: estimatedMsRemaining / 1000 };
}
